import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';
import { TOUR_STEPS, useTourPosition } from './onboarding/useTourPosition';
import { useModalRegistration } from '../contexts/ModalGuardContext';
import useEscapeKey from '../hooks/useEscapeKey';
import SimpleTooltip from './SimpleTooltip';

const OnboardingTour = ({ isActive, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const position = useTourPosition(isActive, currentStep);
  useModalRegistration('onboarding-tour', isActive);
  useEscapeKey(onComplete, isActive);

  if (!isActive) return null;

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const handleNext = () => { if (isLastStep) onComplete(); else setCurrentStep(currentStep + 1); };
  const handlePrevious = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  return (
    <>
      <div className="fixed inset-0 bg-linear-to-br from-black/40 to-black/30 backdrop-blur-xs z-999" onClick={onComplete} />
      <Card className="fixed z-1001 shadow-2xl border-2 border-emerald-400 bg-linear-to-br from-white to-emerald-50/30 animate-in fade-in slide-in-from-bottom-4 duration-300"
        style={{ top: `${position.top}px`, left: `${position.left}px`, width: '400px', maxWidth: 'calc(100vw - 24px)' }}>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                <span className="text-[10px] sm:text-xs font-bold text-emerald-600 uppercase tracking-wide">Step {currentStep + 1} of {TOUR_STEPS.length}</span>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-slate-900 leading-tight">{step.title}</h3>
            </div>
            <SimpleTooltip content="Close tour">
              <Button variant="ghost" size="icon" aria-label="Close tour" className="h-6 w-6 sm:h-7 sm:w-7 -mt-1 -mr-1 hover:bg-red-50 hover:text-red-600 rounded-full shrink-0" onClick={onComplete}>
                <X className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </SimpleTooltip>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 mb-4 sm:mb-5 leading-relaxed">{step.description}</p>
          <div className="flex items-center justify-center space-x-1.5 sm:space-x-2 mb-4 sm:mb-5">
            {TOUR_STEPS.map((_, idx) => (
              <div key={`tour-${idx}`} className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                idx === currentStep ? 'w-6 sm:w-8 bg-linear-to-r from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/50'
                : idx < currentStep ? 'w-1.5 sm:w-2 bg-emerald-400' : 'w-1.5 sm:w-2 bg-slate-300'}`} />
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentStep === 0}
              className="flex-1 border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 transition-all disabled:opacity-40 text-xs sm:text-sm px-2 sm:px-3">
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" /><span className="hidden sm:inline">Previous</span><span className="sm:hidden">Prev</span>
            </Button>
            <Button onClick={handleNext} size="sm"
              className="flex-1 bg-linear-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg shadow-emerald-500/30 transition-all text-xs sm:text-sm px-2 sm:px-3">
              {isLastStep ? (<><Check className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" /><span className="hidden sm:inline">Got It!</span><span className="sm:hidden">Done</span></>)
                : (<><span>Next</span><ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 sm:ml-1" /></>)}
            </Button>
          </div>
          <button onClick={onComplete} className="w-full text-center text-[10px] sm:text-xs text-slate-500 hover:text-slate-700 mt-3 sm:mt-4 font-medium transition-colors">Skip tour</button>
        </CardContent>
      </Card>
    </>
  );
};

export default OnboardingTour;
